class ArticleToc extends HTMLElement {
  constructor() {
    super();
    this.onScrollBound = this.onScroll.bind(this);
    this.onResizeBound = this.onResize.bind(this);
    this.scrollTicking = false;
    this.activeIndex = -1;
  }

  connectedCallback() {
    window.wetheme.webcomponentRegistry.register({ key: 'component-article-toc' });
    // Defer so the article content (a later sibling) is in the DOM even when the
    // section is re-rendered by the theme editor
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
    } else {
      setTimeout(() => this.init(), 0);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScrollBound);
    window.removeEventListener('resize', this.onResizeBound);
    if (this.mq && this.syncOpenBound) {
      this.mq.removeEventListener('change', this.syncOpenBound);
    }
  }

  init() {
    this.details = this.querySelector('.article-toc__details');
    this.list = this.querySelector('[data-toc-list]');
    this.layout = this.closest('[data-article-layout]');
    const content = document.getElementById(this.dataset.contentId);
    if (!content || !this.details || !this.list) return;

    // Only index headings that belong to the article body, not app blocks
    this.headings = Array.from(content.querySelectorAll('h2')).filter(
      (heading) => !heading.closest('.shopify-app-block') && heading.textContent.trim() !== ''
    );

    if (this.headings.length === 0) {
      if (this.layout) this.layout.classList.add('article-layout--no-h2');
      this.hidden = true;
      return;
    }

    this.assignHeadingIds();
    this.buildList();

    this.setStickyOffset();
    window.addEventListener('resize', this.onResizeBound);

    // Open on desktop, collapsed on mobile
    this.mq = window.matchMedia('(min-width: 1024px)');
    this.syncOpenBound = () => {
      this.details.open = this.mq.matches;
    };
    this.syncOpenBound();
    this.mq.addEventListener('change', this.syncOpenBound);

    window.addEventListener('scroll', this.onScrollBound, { passive: true });
    this.updateActiveLink();
  }

  assignHeadingIds() {
    const used = new Set();
    this.headings.forEach((heading) => {
      if (!heading.id) {
        const slug =
          heading.textContent
            .trim()
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .slice(0, 60) || 'section';
        let unique = slug;
        let counter = 2;
        while (used.has(unique) || document.getElementById(unique)) {
          unique = `${slug}-${counter}`;
          counter += 1;
        }
        heading.id = unique;
      }
      used.add(heading.id);
    });
  }

  buildList() {
    const fragment = document.createDocumentFragment();
    this.links = this.headings.map((heading) => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.className = 'article-toc__link';
      link.href = `#${heading.id}`;
      link.textContent = heading.textContent.trim();
      link.addEventListener('click', (event) => {
        event.preventDefault();
        heading.scrollIntoView({ behavior: 'smooth' });
        history.replaceState(null, '', `#${heading.id}`);
        if (!this.mq.matches) this.details.open = false;
      });
      item.appendChild(link);
      fragment.appendChild(item);
      return link;
    });
    this.list.textContent = '';
    this.list.appendChild(fragment);
  }

  setStickyOffset() {
    const stickyHeader = document.querySelector('[data-header-sticky="true"]');
    const height = stickyHeader ? stickyHeader.getBoundingClientRect().height : 0;
    if (this.layout) this.layout.style.setProperty('--article-sticky-offset', `${height}px`);
    this.stickyOffset = height;
  }

  onResize() {
    this.setStickyOffset();
    this.updateActiveLink();
  }

  onScroll() {
    if (this.scrollTicking) return;
    this.scrollTicking = true;
    window.requestAnimationFrame(() => {
      this.scrollTicking = false;
      this.updateActiveLink();
    });
  }

  updateActiveLink() {
    // Active section = last heading at or above the reading line; default to the first
    const readingLine = (this.stickyOffset || 0) + 32;
    let index = 0;
    for (let i = 0; i < this.headings.length; i += 1) {
      if (this.headings[i].getBoundingClientRect().top <= readingLine) {
        index = i;
      } else {
        break;
      }
    }
    if (index === this.activeIndex) return;
    if (this.activeIndex >= 0 && this.links[this.activeIndex]) {
      this.links[this.activeIndex].classList.remove('is-active');
    }
    this.links[index].classList.add('is-active');
    this.activeIndex = index;
  }
}

if (!customElements.get('article-toc')) {
  customElements.define('article-toc', ArticleToc);
}
